/**
 * Comprehensive Golf Shaft Database
 * 
 * Real shaft specifications for accurate club fitting recommendations
 * Includes driver, fairway wood, hybrid, and iron shafts
 */

export interface ShaftSpecs {
  id: string;
  brand: string;
  model: string;
  type: 'driver' | 'fairway' | 'hybrid' | 'iron';
  
  // Construction
  material: 'graphite' | 'steel' | 'multi-material';
  
  // Weight options (grams)
  weights: number[];
  
  // Flex options
  flexOptions: ('L' | 'A' | 'R' | 'S' | 'X' | 'TX')[];
  
  // Performance characteristics
  torque: number; // degrees - lower = stiffer feel
  kickPoint: 'low' | 'mid-low' | 'mid' | 'mid-high' | 'high';
  launch: 'low' | 'mid-low' | 'mid' | 'mid-high' | 'high';
  spin: 'low' | 'mid-low' | 'mid' | 'mid-high' | 'high';
  
  // Player targeting
  swingSpeedRange: {
    min: number;
    max: number;
  };
  playerType: 'tour' | 'better-player' | 'mid-handicap' | 'high-handicap' | 'all';
  
  // Feel characteristics
  feel: 'boardy' | 'firm' | 'medium' | 'smooth' | 'soft';
  stability: number; // 1-10 (10 = most stable)
  
  // Best for
  bestFor: string[];
  notIdealFor?: string[];
  
  // Price tier
  priceTier: 'stock' | 'upgrade' | 'premium' | 'tour';
  priceRange: string;
  
  // Availability
  isCurrentModel: boolean;
  releaseYear: number;
}

/**
 * DRIVER SHAFTS
 */
export const DRIVER_SHAFTS: ShaftSpecs[] = [
  // PROJECT X HZRDUS SERIES
  {
    id: 'hzrdus-smoke-black',
    brand: 'Project X',
    model: 'HZRDUS Smoke Black',
    type: 'driver',
    material: 'graphite',
    weights: [60, 70, 80],
    flexOptions: ['R', 'S', 'X', 'TX'],
    torque: 3.2,
    kickPoint: 'low',
    launch: 'mid-low',
    spin: 'low',
    swingSpeedRange: { min: 100, max: 125 },
    playerType: 'tour',
    feel: 'firm',
    stability: 9,
    bestFor: ['low spin seekers', 'fast swingers', 'better players'],
    notIdealFor: ['high handicappers', 'slow swing speeds'],
    priceTier: 'premium',
    priceRange: '$350-400',
    isCurrentModel: true,
    releaseYear: 2022
  },
  {
    id: 'hzrdus-smoke-rdx-red',
    brand: 'Project X',
    model: 'HZRDUS Smoke RDX Red',
    type: 'driver',
    material: 'graphite',
    weights: [50, 60, 70],
    flexOptions: ['A', 'R', 'S', 'X'],
    torque: 4.5,
    kickPoint: 'mid',
    launch: 'mid-high',
    spin: 'mid',
    swingSpeedRange: { min: 85, max: 105 },
    playerType: 'mid-handicap',
    feel: 'medium',
    stability: 7,
    bestFor: ['mid handicappers', 'moderate swing speeds', 'distance seekers'],
    priceTier: 'upgrade',
    priceRange: '$250-300',
    isCurrentModel: true,
    releaseYear: 2023
  },
  {
    id: 'hzrdus-gen4',
    brand: 'Project X',
    model: 'HZRDUS Gen4',
    type: 'driver',
    material: 'graphite',
    weights: [60, 70, 80],
    flexOptions: ['S', 'X', 'TX'],
    torque: 3.0,
    kickPoint: 'low',
    launch: 'mid-low',
    spin: 'low',
    swingSpeedRange: { min: 105, max: 130 },
    playerType: 'tour',
    feel: 'firm',
    stability: 10,
    bestFor: ['tour players', 'max stability', 'low spin'],
    notIdealFor: ['slower swingers', 'high launch seekers'],
    priceTier: 'tour',
    priceRange: '$400-450',
    isCurrentModel: true,
    releaseYear: 2024
  },
  
  // FUJIKURA VENTUS SERIES
  {
    id: 'ventus-tr-blue',
    brand: 'Fujikura',
    model: 'Ventus TR Blue',
    type: 'driver',
    material: 'graphite',
    weights: [50, 60, 70, 80],
    flexOptions: ['R', 'S', 'X', 'TX'],
    torque: 3.3,
    kickPoint: 'mid',
    launch: 'mid',
    spin: 'mid-low',
    swingSpeedRange: { min: 95, max: 120 },
    playerType: 'better-player',
    feel: 'medium',
    stability: 9,
    bestFor: ['versatility', 'mid-launch low-spin', 'consistent feel'],
    priceTier: 'premium',
    priceRange: '$350-400',
    isCurrentModel: true,
    releaseYear: 2023
  },
  {
    id: 'ventus-tr-red',
    brand: 'Fujikura',
    model: 'Ventus TR Red',
    type: 'driver',
    material: 'graphite',
    weights: [50, 60, 70],
    flexOptions: ['A', 'R', 'S', 'X'],
    torque: 4.8,
    kickPoint: 'high',
    launch: 'high',
    spin: 'mid-high',
    swingSpeedRange: { min: 75, max: 100 },
    playerType: 'high-handicap',
    feel: 'smooth',
    stability: 6,
    bestFor: ['high launch', 'slower swingers', 'max carry'],
    priceTier: 'upgrade',
    priceRange: '$250-300',
    isCurrentModel: true,
    releaseYear: 2023
  },
  {
    id: 'ventus-tr-black',
    brand: 'Fujikura',
    model: 'Ventus TR Black',
    type: 'driver',
    material: 'graphite',
    weights: [60, 70, 80],
    flexOptions: ['S', 'X', 'TX'],
    torque: 2.8,
    kickPoint: 'low',
    launch: 'low',
    spin: 'low',
    swingSpeedRange: { min: 105, max: 130 },
    playerType: 'tour',
    feel: 'boardy',
    stability: 10,
    bestFor: ['lowest spin', 'tour players', 'penetrating ball flight'],
    notIdealFor: ['anyone needing launch help'],
    priceTier: 'tour',
    priceRange: '$400-450',
    isCurrentModel: true,
    releaseYear: 2023
  },
  {
    id: 'speeder-nx',
    brand: 'Fujikura',
    model: 'Speeder NX',
    type: 'driver',
    material: 'graphite',
    weights: [50, 60, 70],
    flexOptions: ['R', 'S', 'X'],
    torque: 4.2,
    kickPoint: 'mid-high',
    launch: 'mid-high',
    spin: 'mid',
    swingSpeedRange: { min: 85, max: 110 },
    playerType: 'mid-handicap',
    feel: 'smooth',
    stability: 7,
    bestFor: ['smooth tempo', 'effortless power', 'balanced performance'],
    priceTier: 'upgrade',
    priceRange: '$250-300',
    isCurrentModel: true,
    releaseYear: 2024
  },
  
  // MITSUBISHI TENSEI SERIES
  {
    id: 'tensei-av-raw-white',
    brand: 'Mitsubishi',
    model: 'Tensei AV Raw White',
    type: 'driver',
    material: 'graphite',
    weights: [55, 65, 75],
    flexOptions: ['R', 'S', 'X'],
    torque: 4.0,
    kickPoint: 'mid',
    launch: 'mid-high',
    spin: 'mid',
    swingSpeedRange: { min: 85, max: 110 },
    playerType: 'mid-handicap',
    feel: 'medium',
    stability: 8,
    bestFor: ['versatility', 'balanced launch/spin', 'smooth feel'],
    priceTier: 'premium',
    priceRange: '$300-350',
    isCurrentModel: true,
    releaseYear: 2023
  },
  {
    id: 'tensei-av-raw-blue',
    brand: 'Mitsubishi',
    model: 'Tensei AV Raw Blue',
    type: 'driver',
    material: 'graphite',
    weights: [55, 65, 75],
    flexOptions: ['R', 'S', 'X'],
    torque: 3.5,
    kickPoint: 'mid',
    launch: 'mid',
    spin: 'mid-low',
    swingSpeedRange: { min: 95, max: 115 },
    playerType: 'better-player',
    feel: 'firm',
    stability: 9,
    bestFor: ['control', 'lower spin', 'consistent flight'],
    priceTier: 'premium',
    priceRange: '$300-350',
    isCurrentModel: true,
    releaseYear: 2023
  },
  {
    id: 'tensei-pro-orange',
    brand: 'Mitsubishi',
    model: 'Tensei Pro Orange',
    type: 'driver',
    material: 'graphite',
    weights: [60, 70, 80],
    flexOptions: ['S', 'X', 'TX'],
    torque: 3.2,
    kickPoint: 'mid-low',
    launch: 'mid',
    spin: 'mid-low',
    swingSpeedRange: { min: 100, max: 125 },
    playerType: 'tour',
    feel: 'firm',
    stability: 9,
    bestFor: ['tour feel', 'workability', 'controlled flight'],
    priceTier: 'tour',
    priceRange: '$400-450',
    isCurrentModel: true,
    releaseYear: 2024
  },
  {
    id: 'diamana-s-plus',
    brand: 'Mitsubishi',
    model: 'Diamana S+ Limited',
    type: 'driver',
    material: 'graphite',
    weights: [60, 70, 80],
    flexOptions: ['S', 'X', 'TX'],
    torque: 3.0,
    kickPoint: 'mid',
    launch: 'mid-low',
    spin: 'low',
    swingSpeedRange: { min: 105, max: 130 },
    playerType: 'tour',
    feel: 'firm',
    stability: 10,
    bestFor: ['tour players', 'shot shaping', 'low spin'],
    notIdealFor: ['high handicappers'],
    priceTier: 'tour',
    priceRange: '$450-500',
    isCurrentModel: true,
    releaseYear: 2023
  },
  {
    id: 'kaili-white',
    brand: 'Mitsubishi',
    model: "Kai'li White",
    type: 'driver',
    material: 'graphite',
    weights: [60, 70, 80],
    flexOptions: ['S', 'X', 'TX'],
    torque: 3.3,
    kickPoint: 'mid',
    launch: 'mid',
    spin: 'mid-low',
    swingSpeedRange: { min: 100, max: 120 },
    playerType: 'better-player',
    feel: 'medium',
    stability: 9,
    bestFor: ['smooth transition', 'versatility', 'tour performance'],
    priceTier: 'premium',
    priceRange: '$350-400',
    isCurrentModel: true,
    releaseYear: 2024
  },
  
  // ALDILA SERIES
  {
    id: 'aldila-ascent',
    brand: 'Aldila',
    model: 'Ascent',
    type: 'driver',
    material: 'graphite',
    weights: [45, 55, 65],
    flexOptions: ['L', 'A', 'R', 'S'],
    torque: 5.0,
    kickPoint: 'high',
    launch: 'high',
    spin: 'mid-high',
    swingSpeedRange: { min: 65, max: 95 },
    playerType: 'high-handicap',
    feel: 'soft',
    stability: 5,
    bestFor: ['seniors', 'high launch needed', 'slower swingers'],
    priceTier: 'stock',
    priceRange: '$100-150',
    isCurrentModel: true,
    releaseYear: 2022
  },
  
  // GRAPHITE DESIGN SERIES
  {
    id: 'tour-ad-di',
    brand: 'Graphite Design',
    model: 'Tour AD DI',
    type: 'driver',
    material: 'graphite',
    weights: [50, 60, 70, 80],
    flexOptions: ['R', 'S', 'X', 'TX'],
    torque: 3.5,
    kickPoint: 'mid',
    launch: 'mid',
    spin: 'mid',
    swingSpeedRange: { min: 90, max: 120 },
    playerType: 'better-player',
    feel: 'medium',
    stability: 8,
    bestFor: ['versatility', 'tour-quality feel', 'consistent performance'],
    priceTier: 'premium',
    priceRange: '$350-400',
    isCurrentModel: true,
    releaseYear: 2023
  },
  
  // STOCK SHAFTS
  {
    id: 'ping-alta-cb-black',
    brand: 'PING',
    model: 'ALTA CB Black',
    type: 'driver',
    material: 'graphite',
    weights: [55, 65],
    flexOptions: ['R', 'S', 'X'],
    torque: 4.5,
    kickPoint: 'mid-high',
    launch: 'mid-high',
    spin: 'mid',
    swingSpeedRange: { min: 80, max: 105 },
    playerType: 'mid-handicap',
    feel: 'smooth',
    stability: 7,
    bestFor: ['PING drivers', 'easy launch', 'balanced performance'],
    priceTier: 'stock',
    priceRange: '$0 (stock)',
    isCurrentModel: true,
    releaseYear: 2023
  },
  {
    id: 'project-x-cypher',
    brand: 'Project X',
    model: 'Cypher',
    type: 'driver',
    material: 'graphite',
    weights: [40, 50, 60],
    flexOptions: ['L', 'A', 'R', 'S'],
    torque: 5.5,
    kickPoint: 'high',
    launch: 'high',
    spin: 'mid-high',
    swingSpeedRange: { min: 60, max: 90 },
    playerType: 'high-handicap',
    feel: 'soft',
    stability: 5,
    bestFor: ['beginners', 'seniors', 'max launch'],
    priceTier: 'stock',
    priceRange: '$0-100',
    isCurrentModel: true,
    releaseYear: 2022
  },
];

/**
 * IRON SHAFTS
 */
export const IRON_SHAFTS: ShaftSpecs[] = [
  // STEEL SHAFTS
  {
    id: 'dynamic-gold-120',
    brand: 'True Temper',
    model: 'Dynamic Gold 120',
    type: 'iron',
    material: 'steel',
    weights: [120, 125, 130],
    flexOptions: ['R', 'S', 'X'],
    torque: 1.7,
    kickPoint: 'low',
    launch: 'mid-low',
    spin: 'mid',
    swingSpeedRange: { min: 90, max: 120 },
    playerType: 'better-player',
    feel: 'firm',
    stability: 10,
    bestFor: ['tour players', 'consistent feel', 'control'],
    notIdealFor: ['seniors', 'slow swing speeds'],
    priceTier: 'upgrade',
    priceRange: '$25-35 per shaft',
    isCurrentModel: true,
    releaseYear: 2020
  },
  {
    id: 'dynamic-gold-105',
    brand: 'True Temper',
    model: 'Dynamic Gold 105',
    type: 'iron',
    material: 'steel',
    weights: [105, 110],
    flexOptions: ['R', 'S'],
    torque: 1.9,
    kickPoint: 'mid',
    launch: 'mid',
    spin: 'mid',
    swingSpeedRange: { min: 80, max: 105 },
    playerType: 'mid-handicap',
    feel: 'medium',
    stability: 8,
    bestFor: ['mid handicappers', 'balanced performance', 'forgiveness'],
    priceTier: 'upgrade',
    priceRange: '$20-30 per shaft',
    isCurrentModel: true,
    releaseYear: 2021
  },
  {
    id: 'ns-pro-modus3-tour-120',
    brand: 'Nippon',
    model: 'NS Pro Modus3 Tour 120',
    type: 'iron',
    material: 'steel',
    weights: [117, 120, 125],
    flexOptions: ['R', 'S', 'X'],
    torque: 1.6,
    kickPoint: 'mid',
    launch: 'mid',
    spin: 'mid',
    swingSpeedRange: { min: 90, max: 115 },
    playerType: 'better-player',
    feel: 'medium',
    stability: 9,
    bestFor: ['smooth feel', 'tour performance', 'consistency'],
    priceTier: 'premium',
    priceRange: '$35-45 per shaft',
    isCurrentModel: true,
    releaseYear: 2022
  },
  {
    id: 'ns-pro-modus3-105',
    brand: 'Nippon',
    model: 'NS Pro Modus3 Tour 105',
    type: 'iron',
    material: 'steel',
    weights: [102, 105, 108],
    flexOptions: ['R', 'S', 'X'],
    torque: 1.8,
    kickPoint: 'mid',
    launch: 'mid-high',
    spin: 'mid',
    swingSpeedRange: { min: 80, max: 105 },
    playerType: 'mid-handicap',
    feel: 'smooth',
    stability: 8,
    bestFor: ['lighter steel feel', 'moderate swing speeds', 'easier launch'],
    priceTier: 'upgrade',
    priceRange: '$30-40 per shaft',
    isCurrentModel: true,
    releaseYear: 2021
  },
  {
    id: 'kbs-tour',
    brand: 'KBS',
    model: 'Tour',
    type: 'iron',
    material: 'steel',
    weights: [120, 125, 130],
    flexOptions: ['R', 'S', 'X'],
    torque: 1.6,
    kickPoint: 'mid',
    launch: 'mid',
    spin: 'mid',
    swingSpeedRange: { min: 90, max: 120 },
    playerType: 'better-player',
    feel: 'firm',
    stability: 9,
    bestFor: ['control', 'consistency', 'tour players'],
    priceTier: 'upgrade',
    priceRange: '$30-40 per shaft',
    isCurrentModel: true,
    releaseYear: 2019
  },
  {
    id: 'kbs-tour-lite',
    brand: 'KBS',
    model: 'Tour Lite',
    type: 'iron',
    material: 'steel',
    weights: [95, 100, 105],
    flexOptions: ['R', 'S'],
    torque: 2.0,
    kickPoint: 'mid-high',
    launch: 'mid-high',
    spin: 'mid',
    swingSpeedRange: { min: 75, max: 95 },
    playerType: 'mid-handicap',
    feel: 'medium',
    stability: 7,
    bestFor: ['moderate swing speeds', 'easier launch', 'lighter feel'],
    priceTier: 'upgrade',
    priceRange: '$25-35 per shaft',
    isCurrentModel: true,
    releaseYear: 2020
  },
  {
    id: 'project-x-ls',
    brand: 'Project X',
    model: 'LS',
    type: 'iron',
    material: 'steel',
    weights: [115, 120, 125],
    flexOptions: ['S', 'X'],
    torque: 1.5,
    kickPoint: 'low',
    launch: 'low',
    spin: 'low',
    swingSpeedRange: { min: 95, max: 125 },
    playerType: 'tour',
    feel: 'boardy',
    stability: 10,
    bestFor: ['low spin', 'fast swingers', 'penetrating flight'],
    notIdealFor: ['high handicappers', 'slow swingers'],
    priceTier: 'premium',
    priceRange: '$40-50 per shaft',
    isCurrentModel: true,
    releaseYear: 2021
  },
  
  // GRAPHITE IRON SHAFTS
  {
    id: 'recoil-esx',
    brand: 'UST',
    model: 'Recoil ESX',
    type: 'iron',
    material: 'graphite',
    weights: [65, 75, 85, 95],
    flexOptions: ['A', 'R', 'S'],
    torque: 3.2,
    kickPoint: 'mid',
    launch: 'mid-high',
    spin: 'mid',
    swingSpeedRange: { min: 70, max: 95 },
    playerType: 'high-handicap',
    feel: 'smooth',
    stability: 6,
    bestFor: ['seniors', 'lighter weight', 'vibration dampening'],
    priceTier: 'upgrade',
    priceRange: '$50-70 per shaft',
    isCurrentModel: true,
    releaseYear: 2022
  },
  {
    id: 'steelfiber-fc',
    brand: 'Aerotech',
    model: 'SteelFiber fc',
    type: 'iron',
    material: 'multi-material',
    weights: [80, 90, 100, 110],
    flexOptions: ['R', 'S', 'X'],
    torque: 2.2,
    kickPoint: 'mid',
    launch: 'mid',
    spin: 'mid',
    swingSpeedRange: { min: 80, max: 110 },
    playerType: 'better-player',
    feel: 'medium',
    stability: 8,
    bestFor: ['steel feel with graphite forgiveness', 'joint issues', 'consistent performance'],
    priceTier: 'premium',
    priceRange: '$70-90 per shaft',
    isCurrentModel: true,
    releaseYear: 2023
  },
  {
    id: 'mmt-taper',
    brand: 'Fujikura',
    model: 'MMT Taper',
    type: 'iron',
    material: 'graphite',
    weights: [80, 95, 105],
    flexOptions: ['R', 'S', 'X'],
    torque: 2.5,
    kickPoint: 'mid',
    launch: 'mid',
    spin: 'mid',
    swingSpeedRange: { min: 85, max: 110 },
    playerType: 'better-player',
    feel: 'medium',
    stability: 8,
    bestFor: ['transition from steel', 'consistent weight feel', 'smoother impact'],
    priceTier: 'premium',
    priceRange: '$60-80 per shaft',
    isCurrentModel: true,
    releaseYear: 2022
  },
];

/**
 * UTILITY FUNCTIONS
 */

// Get recommended shaft based on swing characteristics
export function getRecommendedShaft(params: {
  clubType: 'driver' | 'iron';
  swingSpeed: number;
  tempo: 'slow' | 'medium' | 'fast';
  ballFlight: 'slice' | 'fade' | 'straight' | 'draw' | 'hook';
  priority: 'distance' | 'control' | 'feel' | 'forgiveness';
  handicap?: number;
}): ShaftSpecs[] {
  const { clubType, swingSpeed, tempo, ballFlight, priority, handicap = 18 } = params;
  
  const shafts = clubType === 'driver' ? DRIVER_SHAFTS : IRON_SHAFTS;
  
  return shafts
    .filter(shaft => {
      // Filter by swing speed compatibility
      return swingSpeed >= shaft.swingSpeedRange.min && swingSpeed <= shaft.swingSpeedRange.max;
    })
    .map(shaft => {
      let score = 0;
      
      // Score based on tempo match
      if (tempo === 'slow' && (shaft.feel === 'soft' || shaft.feel === 'smooth')) score += 3;
      if (tempo === 'medium' && shaft.feel === 'medium') score += 3;
      if (tempo === 'fast' && (shaft.feel === 'firm' || shaft.feel === 'boardy')) score += 3;
      
      // Score based on ball flight needs
      if ((ballFlight === 'slice' || ballFlight === 'fade') && shaft.launch === 'high') score += 2;
      if (ballFlight === 'straight' && shaft.launch === 'mid') score += 2;
      if ((ballFlight === 'hook' || ballFlight === 'draw') && shaft.spin === 'low') score += 2;
      
      // Score based on priority
      if (priority === 'distance' && shaft.spin === 'low') score += 2;
      if (priority === 'control' && shaft.stability >= 8) score += 2;
      if (priority === 'feel' && (shaft.feel === 'smooth' || shaft.feel === 'medium')) score += 2;
      if (priority === 'forgiveness' && shaft.launch === 'high') score += 2;
      
      // Score based on handicap
      if (handicap <= 5 && shaft.playerType === 'tour') score += 2;
      if (handicap > 5 && handicap <= 15 && shaft.playerType === 'better-player') score += 2;
      if (handicap > 15 && handicap <= 25 && shaft.playerType === 'mid-handicap') score += 2;
      if (handicap > 25 && shaft.playerType === 'high-handicap') score += 2;
      
      return { ...shaft, matchScore: score };
    })
    .sort((a, b) => (b as any).matchScore - (a as any).matchScore)
    .slice(0, 5);
}

// Get optimal flex based on swing speed
// NOTE: Input swingSpeed is DRIVER swing speed for all club types
export function getRecommendedFlex(swingSpeed: number, clubType: 'driver' | 'iron'): string {
  if (clubType === 'driver') {
    if (swingSpeed >= 110) return 'X';
    if (swingSpeed >= 100) return 'S';
    if (swingSpeed >= 90) return 'R';
    if (swingSpeed >= 80) return 'A';
    return 'L';
  } else {
    // Iron swing speed is typically 10-15% slower than driver
    // Thresholds adjusted to use DRIVER swing speed as input
    // (e.g., 105mph driver = ~90-95mph iron swing = Stiff)
    if (swingSpeed >= 110) return 'X';  // Iron speed ~95-100 = X-Stiff
    if (swingSpeed >= 100) return 'S';  // Iron speed ~85-90 = Stiff  
    if (swingSpeed >= 90) return 'R';   // Iron speed ~75-80 = Regular
    if (swingSpeed >= 80) return 'A';   // Iron speed ~65-70 = Senior
    return 'L';
  }
}

// Get optimal shaft weight based on swing speed and tempo
export function getRecommendedShaftWeight(swingSpeed: number, tempo: 'slow' | 'medium' | 'fast', clubType: 'driver' | 'iron'): number {
  if (clubType === 'driver') {
    // Base weight on swing speed
    let baseWeight = swingSpeed < 90 ? 50 : swingSpeed < 100 ? 60 : swingSpeed < 110 ? 70 : 80;
    
    // Adjust for tempo
    if (tempo === 'slow') baseWeight -= 5;
    if (tempo === 'fast') baseWeight += 5;
    
    return baseWeight;
  } else {
    // Iron shaft weights
    let baseWeight = swingSpeed < 80 ? 85 : swingSpeed < 90 ? 95 : swingSpeed < 100 ? 105 : 120;
    
    if (tempo === 'slow') baseWeight -= 10;
    if (tempo === 'fast') baseWeight += 10;
    
    return baseWeight;
  }
}

// Get all current model shafts
export function getCurrentShafts(clubType?: 'driver' | 'iron'): ShaftSpecs[] {
  if (clubType === 'driver') {
    return DRIVER_SHAFTS.filter(s => s.isCurrentModel);
  }
  if (clubType === 'iron') {
    return IRON_SHAFTS.filter(s => s.isCurrentModel);
  }
  return [...DRIVER_SHAFTS, ...IRON_SHAFTS].filter(s => s.isCurrentModel);
}

export const ALL_SHAFTS = [...DRIVER_SHAFTS, ...IRON_SHAFTS];

